import * as React from 'react';
import { Alert, Button, Snackbar, TextField, Card, CardContent, Box, CircularProgress } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { UserInterface } from "../../../interface/User.interface";
import {
    ADMIN_VIEWS,
    defaultUser,
    EMAIL_REG_EXPRESSION,
    REGISTRATION_URL,
    NAME_REG_EXPRESSION,
    NAME_MIN_LENGTH,
    NAME_MAX_LENGTH
} from "../../../utils/constant";
import useAxios from "../../../hooks/useAxios";
import { CommonContext } from "../../../contexts/CommonContext";
import { PersonAdd, ArrowBack } from "@mui/icons-material";
import styled from "styled-components";
import { logger } from '../../../utils/logger';

interface NewUserFormProps {
    selectedMenu: (view: number) => void;
}


// Styled Components harmonieux avec le design existant
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  animation: fadeInUp 0.8s ease-out;
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
  
  .MuiSvgIcon-root {
    color: white;
    font-size: 1.5rem;
  }
`;

const BackButton = styled.button`
  background: rgba(102, 126, 234, 0.1);
  color: var(--primary-color);
  border: 2px solid rgba(102, 126, 234, 0.2);
  border-radius: 12px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(102, 126, 234, 0.15);
    border-color: rgba(102, 126, 234, 0.3);
    transform: translateY(-2px);
  }
`;

const FormCard = styled(Card)`
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px);
  border-radius: 20px !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  max-width: 600px;
  margin: 0 auto;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1) !important;
  }
`;

const FormHeader = styled.div`
  padding: 1.5rem 1.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const FormTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
`;

const FormSubtitle = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0;
  opacity: 0.8;
`;

const FormContent = styled(CardContent)`
  && {
    padding: 2rem;
  }
`;

const StyledTextField = styled(TextField)`
  && {
    margin-bottom: 1.5rem;
    
    .MuiOutlinedInput-root {
      background: rgba(255, 255, 255, 0.9);
      border-radius: 12px;
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(255, 255, 255, 1);
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
      }
      
      &.Mui-focused {
        background: rgba(255, 255, 255, 1);
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
      }
      
      & .MuiOutlinedInput-notchedOutline {
        border-color: rgba(102, 126, 234, 0.2);
        border-width: 2px;
      }
      
      &:hover .MuiOutlinedInput-notchedOutline {
        border-color: rgba(102, 126, 234, 0.4);
      }
      
      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: var(--primary-color);
      }
    }
    
    .MuiInputLabel-root {
      font-weight: 600;
      color: var(--text-secondary);
      
      &.Mui-focused {
        color: var(--primary-color);
      }
    }
  }
`;

const SubmitButton = styled(Button)`
  && {
    background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
    color: white;
    border-radius: 12px;
    padding: 1rem 2rem;
    font-weight: 600;
    text-transform: none;
    font-size: 1rem;
    margin-top: 1rem;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    }
    
    &:disabled {
      background: rgba(0, 0, 0, 0.12);
      color: rgba(0, 0, 0, 0.26);
      transform: none;
      box-shadow: none;
    }
  }
`;

const AlertContainer = styled(Box)`
  margin-bottom: 1rem;
  
  .MuiAlert-root {
    border-radius: 12px;
    backdrop-filter: blur(10px);
  }
`;

export default function NewUserForm({ selectedMenu }: NewUserFormProps) {
    const [user, setUser] = useState<UserInterface>(defaultUser);
    const { postWithAxios } = useAxios();
    const formRef = useRef(null);
    const [validationErrors, setValidationError] = useState({ email: '', firstname: '', lastname: '' });
    const [globalError, setGlobalError] = useState('');
    const [globalInfo, setGlobalInfo] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const { setNeedToRefreshData } = useContext(CommonContext);

    /**
     * Génère un mot de passe aléatoire respectant les règles backend :
     * - Minimum 8 caractères
     * - Au moins 1 minuscule
     * - Au moins 1 majuscule
     * - Au moins 1 chiffre
     * - Au moins 1 caractère spécial (!@#$%^&*(),.?":{}|<>)
     */
    const generateSecurePassword = (): string => {
        const lowercase = 'abcdefghijklmnopqrstuvwxyz';
        const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const numbers = '0123456789';
        const specialChars = '!@#$%^&*(),.?":{}|<>';

        // Garantir au moins 1 caractère de chaque catégorie
        const password = [
            lowercase[Math.floor(Math.random() * lowercase.length)],
            uppercase[Math.floor(Math.random() * uppercase.length)],
            numbers[Math.floor(Math.random() * numbers.length)],
            specialChars[Math.floor(Math.random() * specialChars.length)]
        ];

        // Compléter jusqu'à 12 caractères avec des caractères aléatoires de toutes catégories
        const allChars = lowercase + uppercase + numbers + specialChars;
        for (let i = password.length; i < 12; i++) {
            password.push(allChars[Math.floor(Math.random() * allChars.length)]);
        }

        // Mélanger le tableau pour éviter un pattern prévisible
        return password.sort(() => Math.random() - 0.5).join('');
    };

    /**
     * Validation complète alignée avec backend (même pattern que AdminUserEditingModal)
     */
    const validateField = (field: 'email' | 'firstname' | 'lastname', value: string): string => {
        if (field === 'email') {
            if (value.trim() === '') return "L'email est requis";
            if (!EMAIL_REG_EXPRESSION.test(value)) return "Format d'email invalide";
            return '';
        }

        // Validation firstname/lastname
        if (value.trim() === '') return "Ce champ est requis";
        if (value.length < NAME_MIN_LENGTH) return `Minimum ${NAME_MIN_LENGTH} caractères requis`;
        if (value.length > NAME_MAX_LENGTH) return `Maximum ${NAME_MAX_LENGTH} caractères autorisés`;
        if (!NAME_REG_EXPRESSION.test(value)) return "Lettres, espaces, trait d'union et apostrophes uniquement";
        return '';
    };

    useEffect(() => {
        setValidationError({
            firstname: validateField('firstname', user.firstname),
            lastname: validateField('lastname', user.lastname),
            email: validateField('email', user.email)
        });
    }, [user]);

    const register = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsLoading(true);
        setGlobalError('');

        try {
            await postWithAxios(REGISTRATION_URL, {
                email: user.email,
                firstname: user.firstname,
                lastname: user.lastname,
                password: generateSecurePassword()
            });

            setGlobalError("");
            setGlobalInfo("Utilisateur créé avec succès. Un email a été envoyé pour définir son mot de passe.");
            setUser(defaultUser);
            setNeedToRefreshData(true);

            // Redirection après succès
            setTimeout(() => selectedMenu(ADMIN_VIEWS.USER_LIST_VIEW), 2000);
        } catch (err: any) {
            logger.error('Error creating user:', err);

            // Gestion d'erreurs spécifiques (pattern AdminUserEditingModal)
            if (err.message?.includes('Email already exists') || err.message?.includes('already exists') || err.message?.includes('duplicate')) {
                setGlobalError("Cet email est déjà utilisé par un autre utilisateur");
            } else if (err.message?.includes('Access denied') || err.message?.includes('Forbidden')) {
                setGlobalError("Accès refusé : droits administrateur requis");
            } else if (err.message?.includes('validation') || err.message?.includes('invalid')) {
                setGlobalError("Données invalides. Veuillez vérifier les champs.");
            } else {
                setGlobalError(err.message || "Erreur lors de la création de l'utilisateur");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackClick = () => {
        selectedMenu(ADMIN_VIEWS.USER_LIST_VIEW);
    };

    return (
        <FormContainer>
            {/* En-tête de section */}
            <SectionHeader>
                <HeaderLeft>
                    <SectionIcon>
                        <PersonAdd />
                    </SectionIcon>
                    <SectionTitle>Créer un utilisateur</SectionTitle>
                </HeaderLeft>
                <BackButton onClick={handleBackClick}>
                    <ArrowBack />
                    Retour à la liste
                </BackButton>
            </SectionHeader>

            {/* Formulaire */}
            <FormCard>
                <FormHeader>
                    <FormTitle>Informations utilisateur</FormTitle>
                    <FormSubtitle>Saisissez les informations du nouvel utilisateur</FormSubtitle>
                </FormHeader>

                <FormContent>
                    {globalError !== '' && (
                        <AlertContainer>
                            <Alert severity="error">{globalError}</Alert>
                        </AlertContainer>
                    )}

                    <Snackbar
                        open={globalInfo !== ''}
                        autoHideDuration={6000}
                        onClose={() => setGlobalInfo('')}
                        message={globalInfo}
                    />

                    <form ref={formRef}>
                        <StyledTextField
                            fullWidth
                            required
                            variant="outlined"
                            label="Nom de famille"
                            error={validationErrors.lastname !== ''}
                            helperText={validationErrors.lastname}
                            value={user.lastname}
                            onChange={(e) => setUser({ ...user, lastname: e.target.value })}
                            disabled={isLoading}
                        />

                        <StyledTextField
                            fullWidth
                            required
                            variant="outlined"
                            label="Prénom(s)"
                            error={validationErrors.firstname !== ''}
                            helperText={validationErrors.firstname}
                            value={user.firstname}
                            onChange={(e) => setUser({ ...user, firstname: e.target.value })}
                            disabled={isLoading}
                        />

                        <StyledTextField
                            fullWidth
                            required
                            variant="outlined"
                            label="Adresse e-mail"
                            type="email"
                            error={validationErrors.email !== ''}
                            helperText={validationErrors.email}
                            value={user.email}
                            onChange={(e) => setUser({ ...user, email: e.target.value })}
                            disabled={isLoading}
                        />

                        <SubmitButton
                            fullWidth
                            variant="contained"
                            disabled={Object.values(validationErrors).some(it => it !== "") || isLoading}
                            onClick={register}
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <PersonAdd />}
                        >
                            {isLoading ? 'Création en cours...' : 'Créer l\'utilisateur'}
                        </SubmitButton>
                    </form>
                </FormContent>
            </FormCard>
        </FormContainer>
    );
}
